import { api } from "./api";

export type Capabilities = {
  pipeline: "mcp" | "legacy";
  admin: boolean;
  tables: Record<string, boolean>;
  tools: string[];
  examples?: Examples;
};

export type SuggestionRequires = {
  tools?: string[];
  tables?: string[];
  admin?: boolean;
};

/**
 * A single live (or fallback) Cognos example object resolved by the
 * backend's ExampleObjectResolver. Fields are omitted (not null) when
 * the backend has no value for them — {@code isExample} is always
 * present and tells the UI whether this is a real content-store object
 * (false) or the static demo-corpus fallback (true).
 */
export type ExampleRef = {
  name?: string;
  searchPath?: string;
  storeId?: string;
  displayPath?: string;
  isExample: boolean;
};

/**
 * Map of prompt-template slots to their resolved example object, keyed
 * by the slot's jsonKey exactly as produced by Task 5's
 * ExampleObjectResolver. {@code resolvedAt} is the ISO-8601 instant of
 * the last successful resolve, or null before the first resolve
 * completes. A slot is entirely absent from the map when nothing has
 * resolved for it yet (notably {@code recycled}, which stays absent
 * until a real recycle-bin object is found — never a fake store-id).
 */
export type Examples = Partial<
  Record<"report" | "folder" | "package" | "dataModule" | "versionedReport" | "recycled", ExampleRef>
> & { resolvedAt?: string | null };

export async function fetchCapabilities(): Promise<Capabilities> {
  const { data } = await api.get<Capabilities>("/capabilities");
  return data;
}

/**
 * Kicks off a fresh resolve of the example-objects map server-side and
 * returns whatever it has immediately (the resolve itself may still be
 * running — the caller should re-GET /capabilities shortly after to
 * pick up the update once {@code resolvedAt} changes).
 */
export async function refreshExamples(): Promise<Examples | undefined> {
  const { data } = await api.post<{ refreshing: boolean; examples: Examples }>(
    "/capabilities/refresh-examples",
  );
  return data.examples;
}

/**
 * Returns true when the suggestion is plausibly answerable given the
 * capabilities snapshot. Three deliberate concessions to keep the
 * Chat empty-state populated rather than blank:
 *
 * <ol>
 *   <li>Missing {@code caps} (probe hasn't loaded yet) → show the
 *       suggestion. Better an optimistic chip than empty UI.</li>
 *   <li>Missing {@code caps.tools} when the pipeline is "legacy" — the
 *       legacy direct-SQL path doesn't expose MCP tools, but most
 *       prompts still answer through it. Treat the tool requirement
 *       as advisory in that case.</li>
 *   <li>Tables required but {@code caps.tables} is empty — same
 *       reason as above. The /capabilities endpoint probes a fixed
 *       set of overlay tables; an empty result usually means the
 *       probe couldn't connect, not that every table is missing.</li>
 * </ol>
 *
 * The {@code admin} flag is the only hard gate: admin-only chips
 * never show to non-admins.
 */
export function canDeliver(req: SuggestionRequires | undefined, caps: Capabilities | undefined): boolean {
  if (!req) return true;
  if (!caps) return true;
  if (req.admin && !caps.admin) return false;

  const toolsKnown  = Array.isArray(caps.tools) && caps.tools.length > 0;
  const tablesKnown = caps.tables && Object.keys(caps.tables).length > 0;

  if (req.tables && req.tables.length > 0 && tablesKnown) {
    const allMissing = req.tables.every((t) => !caps.tables[t]);
    if (allMissing) return false;
  }
  if (req.tools && req.tools.length > 0 && toolsKnown) {
    const present = new Set(caps.tools);
    const noneMatch = !req.tools.some((t) => present.has(t));
    if (noneMatch) return false;
  }
  return true;
}
